import * as React from "react";
import { Redirect, Route } from "react-router-dom";

import { CarrierDashboard } from "./layout/CarrierDashboard";
import { CompanyProfileEdit } from "./resources/company/CompanyProfileEdit";

import { InboundList } from "./resources/inbounds/InboundList";
import { InboundDispatchEdit } from "./resources/inbounds/InboundDispatchEdit";
import { InboundDispatchScheduleEdit } from "./resources/inbounds/InboundDispatchScheduleEdit";
import { InboundShow } from "./resources/inbounds/InboundShow";

import { OutboundList } from "./resources/outbounds/OutboundList";
import { OutboundDispatchEdit } from "./resources/outbounds/OutboundDispatchEdit";
import { OutboundDispatchScheduleEdit } from "./resources/outbounds/OutboundDispatchScheduleEdit";
import { OutboundShow } from "./resources/outbounds/OutboundShow";
import { isClaimLanding } from "./resources/loadSearch/ClaimLoadPage";

import {
  COMPANY_PATH,
  DASHBOARD_PATH,
  DELIVERIES_PATH,
  PICKUPS_PATH,
} from "./carrierPaths";

// One edit route per shipment resource, two forms behind it: a carrier arriving
// straight from a claim gets the trimmed schedule form so claiming and
// scheduling read as one step, while the same shipment opened from the list gets
// the full dispatch form.
const InboundCarrierEdit = (props) =>
  isClaimLanding(props.location) ? (
    <InboundDispatchScheduleEdit {...props} />
  ) : (
    <InboundDispatchEdit {...props} />
  );

const OutboundCarrierEdit = (props) =>
  isClaimLanding(props.location) ? (
    <OutboundDispatchScheduleEdit {...props} />
  ) : (
    <OutboundDispatchEdit {...props} />
  );

// The props react-admin's own resource routes hand a list/edit/show. basePath
// is the renamed URL — every link react-admin builds (row click, edit and show
// buttons, post-save redirects) derives from it — while resource stays the API
// name the dataProvider dispatches on.
const resourceProps = (name, basePath) => ({
  resource: name,
  basePath,
  hasList: true,
  hasEdit: true,
  hasShow: true,
  hasCreate: false,
});

// Neither the show nor the edit route is `exact`. These forms are single-page
// now, but they were tabbed, and a tab synced itself to the URL by appending
// its index (`/show/1`, `/:id/2`) — so bookmarks and pasted links carrying one
// of those segments are still out there, and an exact match would drop them on
// react-admin's catch-all "Not Found". These routes render inside a Switch, so
// the more specific `/show` route above still wins over the edit route below.
const shipmentRoutes = (basePath, name, List, Edit, Show) => [
  <Route
    path={`${basePath}/:id/show`}
    render={(routeProps) => (
      <Show
        id={decodeURIComponent(routeProps.match.params.id)}
        {...routeProps}
        {...resourceProps(name, basePath)}
      />
    )}
  />,
  <Route
    path={`${basePath}/:id`}
    render={(routeProps) => (
      <Edit
        id={decodeURIComponent(routeProps.match.params.id)}
        {...routeProps}
        {...resourceProps(name, basePath)}
      />
    )}
  />,
  <Route
    exact
    path={basePath}
    render={(routeProps) => (
      <List
        {...routeProps}
        {...resourceProps(name, basePath)}
        syncWithLocation
      />
    )}
  />,
];

// Bookmarks and links carriers were sent before the rename still work.
const renameRedirect = (from, to) => (
  <Route
    path={from}
    render={({ location }) => (
      <Redirect
        to={{ ...location, pathname: location.pathname.replace(from, to) }}
      />
    )}
  />
);

// react-admin renders customRoutes ahead of both the resource routes and its own
// "/" route, so this list is what decides where the carrier lands.
const CarrierRoutes = [
  <Route
    exact
    path="/"
    render={() => <Redirect to={{ pathname: DASHBOARD_PATH }} />}
  />,
  <Route
    path={DASHBOARD_PATH}
    render={(props) => <CarrierDashboard {...props} />}
  />,
  <Route
    path={COMPANY_PATH}
    render={(props) => <CompanyProfileEdit {...props} />}
  />,
  ...shipmentRoutes(
    DELIVERIES_PATH,
    "inboundShipments",
    InboundList,
    InboundCarrierEdit,
    InboundShow,
  ),
  ...shipmentRoutes(
    PICKUPS_PATH,
    "outboundShipments",
    OutboundList,
    OutboundCarrierEdit,
    OutboundShow,
  ),
  renameRedirect("/inboundShipments", DELIVERIES_PATH),
  renameRedirect("/outboundShipments", PICKUPS_PATH),
];

export default CarrierRoutes;
